export type ConsentSource = "cookie" | "banner" | "gpc" | "geo";

export interface ConsentCookie {
    v: 1;
    ts: string;
    analytics: boolean;
    marketing: boolean;
    recording: boolean;
    gpc: boolean;
}

export interface ConsentDetail {
    analytics: boolean;
    marketing: boolean;
    recording: boolean;
    gpc: boolean;
    source: ConsentSource;
}

export interface ConsentInput {
    analytics: boolean;
    marketing: boolean;
    recording: boolean;
}

const COOKIE_NAME = "braiv_consent";
const COOKIE_DOMAIN = ".braiv.co";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 183;
const VERSION = 1;

function getCookieDomain(): string | null {
    if (typeof window === "undefined") return null;
    const host = window.location.hostname;
    if (host === "braiv.co" || host.endsWith(".braiv.co")) {
        return COOKIE_DOMAIN;
    }
    return null;
}

export function isGpcActive(): boolean {
    if (typeof navigator === "undefined") return false;
    return (navigator as Navigator & {globalPrivacyControl?: boolean}).globalPrivacyControl === true;
}

export function parseConsentCookie(): ConsentCookie | null {
    if (typeof document === "undefined") return null;

    const escaped = COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    if (!match) return null;

    try {
        const parsed = JSON.parse(decodeURIComponent(match[1])) as ConsentCookie;
        if (parsed?.v === VERSION) return parsed;
    } catch {
        /* ignore malformed cookie */
    }
    return null;
}

function writeCookie(state: ConsentCookie): void {
    if (typeof document === "undefined") return;

    const parts = [
        `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}`,
        "path=/",
        `max-age=${String(COOKIE_MAX_AGE)}`,
        "SameSite=Lax",
    ];
    const domain = getCookieDomain();
    if (domain) parts.push(`domain=${domain}`);
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
        parts.push("Secure");
    }
    document.cookie = parts.join("; ");
}

function dispatchConsent(detail: ConsentDetail): void {
    if (typeof document === "undefined") return;
    document.dispatchEvent(new CustomEvent("braiv:consent", {detail}));
}

function dispatchConsentNeeded(): void {
    if (typeof document === "undefined") return;
    document.dispatchEvent(new CustomEvent("braiv:consent-needed"));
}

export function writeConsentCookie(state: ConsentInput): void {
    const newState: ConsentCookie = {
        v: VERSION,
        ts: new Date().toISOString(),
        analytics: !!state.analytics,
        marketing: !!state.marketing,
        recording: !!state.recording,
        gpc: false,
    };
    writeCookie(newState);
    dispatchConsent({
        analytics: newState.analytics,
        marketing: newState.marketing,
        recording: newState.recording,
        gpc: false,
        source: "banner",
    });
}

export function hasAnalyticsConsent(): boolean {
    if (isGpcActive()) return false;
    return !!parseConsentCookie()?.analytics;
}

/** Apply GPC defaults or emit existing consent on first client load. */
export function bootstrapConsent(): void {
    if (typeof window === "undefined") return;

    if (isGpcActive()) {
        const gpcState: ConsentCookie = {
            v: VERSION,
            ts: new Date().toISOString(),
            analytics: false,
            marketing: false,
            recording: false,
            gpc: true,
        };
        writeCookie(gpcState);
        dispatchConsent({
            analytics: false,
            marketing: false,
            recording: false,
            gpc: true,
            source: "gpc",
        });
        return;
    }

    const existing = parseConsentCookie();
    if (existing) {
        dispatchConsent({
            analytics: !!existing.analytics,
            marketing: !!existing.marketing,
            recording: !!existing.recording,
            gpc: !!existing.gpc,
            source: "cookie",
        });
        return;
    }

    dispatchConsentNeeded();
}

/**
 * Geo-aware bootstrap. Call this instead of bootstrapConsent() when the
 * consent tier is known from /api/geo. Handles three-tier GTM loading:
 *
 * - superstrict: no GTM until explicit accept (dispatches consent-needed)
 * - strict:      inject consent mode defaults + load GTM with denied state
 *                (cookieless pings) + show banner
 * - open:        auto-grant analytics for new visitors; no banner
 *
 * GPC is always the hard override regardless of tier.
 * Existing cookies are always honoured — geo logic only applies to new visitors.
 */
export function bootstrapConsentWithGeo(tier: import("@/lib/consentRegions").ConsentTier): void {
    if (typeof window === "undefined") return;

    // 1. GPC hard kill — no GTM, no tracking, regardless of tier
    if (isGpcActive()) {
        const gpcState: ConsentCookie = {
            v: VERSION,
            ts: new Date().toISOString(),
            analytics: false,
            marketing: false,
            recording: false,
            gpc: true,
        };
        writeCookie(gpcState);
        dispatchConsent({analytics: false, marketing: false, recording: false, gpc: true, source: "gpc"});
        return;
    }

    // 2. Existing cookie — replay stored preference
    const existing = parseConsentCookie();
    if (existing) {
        // For strict/open with analytics denied, inject consent defaults so GTM
        // runs in cookieless pings mode rather than being blocked entirely.
        if (!existing.analytics && tier !== "superstrict") {
            import("@/lib/consentModeDefaults").then(({injectConsentDefaults}) => {
                injectConsentDefaults();
            });
        }
        dispatchConsent({
            analytics: !!existing.analytics,
            marketing: !!existing.marketing,
            recording: !!existing.recording,
            gpc: !!existing.gpc,
            source: "cookie",
        });
        return;
    }

    // 3. No cookie — apply tier-based default
    if (tier === "open") {
        // Auto-grant: non-strict region, no banner needed
        const autoState: ConsentCookie = {
            v: VERSION,
            ts: new Date().toISOString(),
            analytics: true,
            marketing: true,
            recording: true,
            gpc: false,
        };
        writeCookie(autoState);
        dispatchConsent({analytics: true, marketing: true, recording: true, gpc: false, source: "geo"});
        return;
    }

    if (tier === "strict") {
        // Inject denied consent defaults so GTM loads with cookieless pings,
        // then show the banner for the user to make a choice.
        import("@/lib/consentModeDefaults").then(({injectConsentDefaults}) => {
            injectConsentDefaults();
            // Signal to _app.tsx that GTM should load in consent-mode (denied) state
            document.dispatchEvent(new CustomEvent("braiv:consent-mode-strict"));
        });
        dispatchConsentNeeded();
        return;
    }

    // superstrict: no GTM until explicit accept
    dispatchConsentNeeded();
}
